import { ajax_json_request } from "@/components/Settings/settings_utils";
import {
    get_auth_headers,
    get_user_friendly_error_message,
    users_url
} from "../settings_user_management_api";
import SettingsMobileListControls from "@/components/SettingsMobileListControls/SettingsMobileListControls.vue";

export default {
    name: "SettingsUserManagementTable",

    components: {
        SettingsMobileListControls
    },

    props: {
        users: {
            type: Array,
            default: () => []
        },
        property_options: {
            type: Array,
            default: () => []
        },
        is_loading: {
            type: Boolean,
            default: false
        },
        has_users_endpoint: {
            type: Boolean,
            default: false
        },
        current_user_id: {
            type: [String, Number],
            default: null
        },
        current_user_email: {
            type: String,
            default: ""
        }
    },

    emits: ["users-updated", "reload-users", "status-message", "error-message"],

    data() {
        return ({
            updating_status_user_id: null,
            mobile_page: 1,
            mobile_page_size: 5,
            mobile_search: "",
            data_table_version: 0
        });
    },

    computed: {
        is_busy() {
            return this.updating_status_user_id !== null;
        },

        mobile_page_count() {
            return Math.max(1, Math.ceil(this.filtered_mobile_users.length / this.mobile_page_size));
        },

        paginated_mobile_users() {
            const start_index = (this.mobile_page - 1) * this.mobile_page_size;
            return this.filtered_mobile_users.slice(start_index, start_index + this.mobile_page_size);
        },

        filtered_mobile_users() {
            const search = this.mobile_search.trim().toLowerCase();

            if (!search) return this.users;

            return this.users.filter((user) => this.get_user_search_text(user).includes(search));
        }
    },

    watch: {
        users() {
            this.mobile_page = 1;
            // DataTables rearranges the tbody, so Vue can't safely patch a changed row list.
            // Tear it down, re-create the whole keyed wrapper, then rebuild on the fresh table.
            this.destroy_data_table();
            this.data_table_version += 1;
            this.$nextTick(this.init_data_table);
        },

        mobile_search() {
            this.mobile_page = 1;
        }
    },

    created() {
        // Saved page/search/order so a rebuild doesn't reset what the user was looking at.
        this.data_table_state = null;
    },

    mounted() {
        this.init_data_table();
    },

    beforeUnmount() {
        this.data_table_state = null;
        window.global_data_table?.destroy_table(this.$refs.data_table);
    },

    methods: {
        get_data_table_api() {
            const table_element = this.$refs.data_table;
            const data_table = window.DataTable;

            return table_element && data_table?.isDataTable?.(table_element)
                ? new data_table(table_element)
                : null;
        },

        init_data_table() {
            const table_element = this.$refs.data_table;
            if (!table_element || !window.global_data_table) return;

            const api = window.global_data_table.init_table(table_element, {
                lengthMenu: [5, 10, 25, 50],
                layout: {
                    topStart: "search",
                    topEnd: "pageLength",
                    bottomStart: "info",
                    bottomEnd: "paging"
                },
                language: {
                    lengthMenu: "_MENU_ per page",
                    info: "Showing _START_ to _END_ of _TOTAL_ users",
                    infoEmpty: "No users to show",
                    infoFiltered: "(filtered from _MAX_)",
                    zeroRecords: "No matching users found.",
                    emptyTable: this.get_empty_table_html()
                }
            });

            this.restore_data_table_state(api);
        },

        destroy_data_table() {
            const api = this.get_data_table_api();

            if (api) {
                this.data_table_state = {
                    length: api.page.len(),
                    order: api.order(),
                    page: api.page(),
                    search: api.search()
                };
            }

            window.global_data_table?.destroy_table(this.$refs.data_table);
        },

        restore_data_table_state(api) {
            const state = this.data_table_state;
            this.data_table_state = null;

            if (!api || !state) return;

            api.page.len(state.length).search(state.search).order(state.order).draw(false);
            api.page(state.page).draw(false);
        },

        get_empty_table_html() {
            return `
                <div class="d-flex flex-column align-items-center py-4">
                    <span class="p-3 bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center justify-content-center rounded-circle mb-3">
                        <i class="bi bi-people-fill"></i>
                    </span>
                    <p class="text-white fw-semibold mb-1">No users available</p>
                    <small class="text-body-secondary">Added users will appear here.</small>
                </div>`;
        },

        get_last_login_time(user) {
            const time = new Date(user?.last_login || "").getTime();
            return Number.isNaN(time) ? 0 : time;
        },

        is_status_action_disabled(user) {
            return this.is_busy
                || (this.is_current_user(user) && user.is_active)
                || (!user.is_active && !this.has_users_endpoint);
        },

        set_mobile_page(page) {
            this.mobile_page = Math.min(Math.max(Number(page), 1), this.mobile_page_count);
        },

        get_user_search_text(user) {
            return [
                this.get_full_name(user),
                user?.email,
                user?.role,
                user?.is_active ? "active" : "inactive",
                this.get_last_login_label(user)
            ].join(" ").toLowerCase();
        },

        open_edit_user(user) {
            sessionStorage.setItem("edit_user_profile", JSON.stringify(user || {}));
            sessionStorage.setItem("edit_user_property_options", JSON.stringify(this.property_options || []));
            this.$router.push({
                path: "/edit_profile",
                query: {
                    user_id: user.id,
                    return_to: "user_management"
                }
            });
        },

        async toggle_user_status(user) {
            if (this.is_self_deactivation(user)) {
                this.$emit("error-message", "You cannot deactivate your own account while you are signed in.");
                return;
            }

            const next_user = this.get_toggled_user(user);
            this.start_status_update(user);

            try {
                await this.persist_user_status(user, next_user);
                this.$emit("status-message", this.get_status_message(next_user));
            } catch (error) {
                this.$emit("error-message", get_user_friendly_error_message(error?.message, "We could not update this user's status. Please try again."));
            } finally {
                this.updating_status_user_id = null;
            }
        },

        is_self_deactivation(user) {
            return this.is_current_user(user) && user.is_active;
        },

        get_toggled_user(user) {
            return ({
                ...user,
                is_active: !user.is_active
            });
        },

        start_status_update(user) {
            this.updating_status_user_id = this.get_user_loading_key(user);
            this.$emit("error-message", "");
            this.$emit("status-message", "");
        },

        is_user_status_updating(user) {
            return this.updating_status_user_id === this.get_user_loading_key(user);
        },

        get_user_loading_key(user) {
            return String(user?.id || user?.email || "");
        },

        should_save_status_to_api(user, next_user) {
            return Boolean(user.id && users_url && next_user.is_active !== undefined);
        },

        async persist_user_status(user, next_user) {
            if (this.should_save_status_to_api(user, next_user)) {
                await this.save_user_status(user, next_user);
                await this.emit_reload_users();
                return;
            }

            this.emit_updated_users(next_user);
        },

        emit_updated_users(next_user) {
            this.$emit("users-updated", this.users.map((existing_user) => {
                return String(existing_user.id) === String(next_user.id) ? next_user : existing_user;
            }));
        },

        get_status_message(next_user) {
            return next_user.is_active ? "User activated." : "User deactivated.";
        },

        async save_user_status(user, next_user) {
            const payload = next_user.is_active
                ? { is_active: next_user.is_active }
                : null;

            await this.fetch_user_status_json(this.get_user_status_url(user, next_user), {
                method: "POST",
                headers: get_auth_headers(true),
                body: payload ? JSON.stringify(payload) : null
            });

            this.emit_updated_users(next_user);
        },

        get_user_status_url(user, next_user) {
            const base_url = this.get_user_update_base_url();
            return `${base_url}${user.id}/${next_user.is_active ? "activate/" : "deactivate/"}`;
        },

        get_user_update_base_url() {
            return users_url.endsWith("/") ? users_url : `${users_url}/`;
        },

        fetch_user_status_json(request_url, options = {}) {
            if (!request_url) {
                throw new Error("User status changes are not fully set up yet.");
            }

            return ajax_json_request(request_url, options);
        },

        emit_reload_users() {
            return new Promise((resolve) => {
                this.$emit("reload-users", resolve);
            });
        },

        get_full_name(user) {
            return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "User";
        },

        get_initials(user) {
            const first = user.first_name?.charAt(0) || "";
            const last = user.last_name?.charAt(0) || "";
            const fallback = user.email?.charAt(0) || "U";
            return (first + last || fallback).toUpperCase();
        },

        get_last_login_label(user) {
            if (!user?.last_login) {
                return "Never";
            }

            const last_login = new Date(user.last_login);
            if (Number.isNaN(last_login.getTime())) {
                return String(user.last_login);
            }

            return last_login.toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            });
        },

        is_current_user(user) {
            const user_id = user?.id || null;
            const user_email = String(user?.email || "").toLowerCase();

            if (this.current_user_id && String(user_id) === String(this.current_user_id)) {
                return true;
            }

            return this.current_user_email ? user_email === this.current_user_email : false;
        },

        get_status_action_title(user) {
            if (this.is_current_user(user) && user.is_active) {
                return "You cannot deactivate your own account";
            }

            return !user.is_active && !this.has_users_endpoint
                ? "Activate endpoint is not available"
                : "";
        }
    }
};
